import { useCallback } from 'react';
import { useAgentStore } from '@/stores/agentStore';
import { createLocalId, formatFinalAssistantText } from '../helpers';
import { useAgentMessages } from './useAgentMessages';

export function useStreamErrorHandler() {
  const { finalizeAssistantMessage } = useAgentMessages();

  const handleError = useCallback(async (
    error: string,
    state: {
      accumulatedText: string;
      currentIntent: { name: string; confidence: number; source: string } | null;
      userText: string;
      currentAttachments: any[];
      currentRefs: any[];
      streamSessionId: string;
      startStreamPolling: (sessionId: string, msgId: string) => void;
    },
  ) => {
    const isConnectionError = error.includes('Stream error') ||
      error.includes('Connection error') ||
      error.includes('network') ||
      error.includes('Failed to fetch') ||
      error.includes('fetch failed') ||
      error.includes('NetworkError') ||
      error.includes('Load failed') ||
      error.includes('disconnected');

    const store = useAgentStore.getState();
    const builder = store.activeReplyBuilder;
    const pendingId = store.pendingBackendMsgIds?.assistant_msg_id;

    // 🔵 v3-#1: 错误处理路径检查 builder.type，搜索/确认场景保留数据
    if (isConnectionError) {
      if (builder?.type === 'search' && builder.searchData) {
        // 🔴 v7-#2: 确保 builder 的 introText 已设置，避免 polling 期间搜索卡片无引言
        if (!builder.introText && builder.searchData) {
          store.updateActiveReplyBuilder({
            introText: builder.searchData.summary || `搜索「${builder.searchData.query || ''}」的结果`,
          });
        }
        // 搜索场景：保留搜索结果，启动 polling 等待后端完成
        if (builder.id || pendingId) {
          state.startStreamPolling(state.streamSessionId, (builder.id || pendingId)!);
          return;
        }
      }
      if (builder?.type === 'generation_confirm' && builder.generationOptions) {
        // 🔴 v7-#2: 确保 builder 的 introText 已设置
        if (!builder.introText && builder.text) {
          store.updateActiveReplyBuilder({
            introText: builder.text,
          });
        }
        // 生成确认场景：保留确认面板，启动 polling
        if (builder.id || pendingId) {
          state.startStreamPolling(state.streamSessionId, (builder.id || pendingId)!);
          return;
        }
      }
      // 文本场景：原有逻辑
      if (state.accumulatedText && pendingId) {
        // 🔴 v8-#5: 清除 builder，避免 polling 期间 UI 显示过时的 builder 数据
        // polling 完成后 resetStreamingState 会再次清除，此处提前清除避免中间状态
        if (store.activeReplyBuilder) {
          store.clearActiveReplyBuilder();
        }
        store.setStreamingText(state.accumulatedText + '\n\n⏳ 后台生成中...');
        state.startStreamPolling(state.streamSessionId, pendingId);
        return;
      }
    }

    // 🔴 v7-#5: 使用已有的后端 ID，避免前端创建的 ID 与后端不一致
    const msgId = builder?.id || pendingId || createLocalId();

    await finalizeAssistantMessage({
      id: msgId,
      text: formatFinalAssistantText({
        text: `抱歉，发生了错误：${error}`,
        intentName: state.currentIntent?.name,
        userText: state.userText,
        hasVisualContext: state.currentAttachments.length > 0 || state.currentRefs.length > 0,
      }),
      type: 'text',
    });
  }, [finalizeAssistantMessage]);

  return { handleError };
}
